import { LEG_AXIS, LEG_BAND } from "../overlay/seatedLeg";
import type { ThighSide } from "../thighSide";
import type { PoseLandmark, PoseReading } from "./types";

/**
 * The injection area, drawn on her own body from what the camera can see.
 *
 * Replaces the diagram that used to float over the preview. A drawing of
 * somebody's abdomen sitting on top of hers has to pick a shape, and whichever
 * it picks is wrong for most people; outlining the area on the body already in
 * the picture does not have to pick one at all. The shape comes from her.
 *
 * ### What it depicts, and what it must not
 *
 * The step says: the lower abdomen, away from the navel. The area is drawn away
 * from the navel; the navel itself is not drawn, because it is not detected —
 * see `AREA_FRACTION`. No number is rendered either, for the same reason no
 * figure carries one: the distance is localised prose and a shape claiming to
 * measure it would outlive the sentence it came from.
 *
 * Every fraction below is a **presentational estimate**, not anatomy. They place
 * an outline over roughly the right region of a body; which spot, and how far
 * from the navel, is in the step's words, which are the reviewed part.
 *
 * ### The crop
 *
 * Landmarks are normalised to the **camera frame**. The preview fills a portrait
 * box with `cover`, so unless the two happen to share an aspect ratio the frame
 * is cropped — and a point at the middle of the frame is not at the middle of
 * the box. `mapFrameToBox` is the correction, and it is separate and tested
 * because without it every outline sits plausibly but consistently off to one
 * side, which is the kind of wrong that gets explained away as camera angle.
 */

/** Below this, a landmark is treated as not seen. */
const MIN_VISIBILITY = 0.6;

/**
 * The shortest torso worth drawing on, as a fraction of frame height.
 *
 * Further away than this and a few pixels of landmark jitter move the outline
 * across her whole abdomen. Better to stop claiming to know.
 */
const MIN_TORSO_HEIGHT = 0.16;

/**
 * The shortest leg worth drawing on, as a fraction of frame width.
 *
 * It borrowed the torso's 0.16, and that number means something on a torso and
 * nothing on a leg: the outline she is asked to sit inside spans about half the
 * frame's width, so a leg a third of that size turned the silhouette green and
 * had the voice tell her she was framed. The abdomen has no such gap because
 * its threshold and its guide are both expressed in torso heights.
 *
 * Half the drawn outline, near enough. Not the whole of it — the silhouette is
 * generous on purpose and matching it exactly would be a measurement, which is
 * what this guide is careful not to ask for.
 */
const MIN_THIGH_LENGTH = 0.26;

/**
 * Whether a landmark is an observation rather than a guess.
 *
 * Confidence alone is not enough, and assuming it was is the bug that made
 * these outlines untrustworthy in use. Pose models extrapolate landmarks past
 * the edge of the frame — a hip that is out of shot still gets coordinates,
 * often with a confidence that passes any sensible threshold — so a close-up of
 * an abdomen produced four "visible" landmarks, none of which the camera had
 * seen, and outlines drawn from all four. That is why she was fighting to
 * centre herself: the overlay claimed to have found her long before it had.
 *
 * A point outside the frame has not been observed, and is refused here.
 */
function isObserved(landmark: PoseLandmark | undefined): boolean {
  if (!landmark) return false;
  if (!Number.isFinite(landmark.x) || !Number.isFinite(landmark.y)) {
    return false;
  }
  if (landmark.x < 0 || landmark.x > 1 || landmark.y < 0 || landmark.y > 1) {
    return false;
  }
  // An absent visibility is "no reason to doubt it" — some detectors do not
  // report one, and treating silence as zero would disable them entirely.
  return (landmark.visibility ?? 1) >= MIN_VISIBILITY;
}

/**
 * The one place the preview's flip is applied.
 *
 * Exported so it can be tested directly and so nobody is tempted to write
 * `1 - x` somewhere else: applied twice it is the identity, which is
 * indistinguishable from never applying it while she stands in the middle of
 * the frame, and wrong by her whole body width once she does not.
 */
export function toMirroredScreenX(landmarkX: number): number {
  return 1 - landmarkX;
}

/**
 * How well the camera can see her, in the ways that need different answers.
 *
 * "Not found" and "too far" look identical from inside the code and call for
 * opposite actions, so they are separate states rather than one boolean.
 *
 * Two of the five are not derivable from a reading and this function never
 * returns them. `unavailable` means the platform has no detector — native has
 * none, and the model can fail to load on web — and `starting` means one exists
 * but has not answered yet. Both were previously collapsed into `searching`,
 * so a patient on a phone was told "we can't see you yet" forever, over a
 * working picture of herself: the overlay asserting an observation it had never
 * made, which is the failure this whole file exists to prevent.
 *
 * `ready` means exactly what `injectionZonesFrom` needs, deliberately: telling
 * her the framing is good and then drawing nothing is worse than never claiming
 * it. A test holds the two together.
 */
export type Framing =
  /** No detector on this platform, or it failed to start. Not a failure of hers. */
  | "unavailable"
  /** A detector exists and has not answered yet. */
  | "starting"
  | "searching"
  | "too-far"
  | "ready";

export function framingFrom(
  reading: PoseReading,
  shape: "torso" | "thigh" = "torso",
  side?: ThighSide | null,
): Framing {
  // A thigh is framed by the limb, not by the body: a seated patient's
  // shoulders are often out of shot, and asking her to fit them in would be
  // asking her to satisfy a check that has nothing to do with where the needle
  // goes. What has to be in view is the hip and the knee the band is drawn
  // between — exactly the landmarks `thighOf` needs.
  if (shape === "thigh") {
    const thigh = thighOf(reading, side);
    if (!thigh) return "searching";
    if (thigh.length < MIN_THIGH_LENGTH) return "too-far";
    return "ready";
  }

  const torso = torsoOf(reading);
  if (!torso) return "searching";
  if (torso.height < MIN_TORSO_HEIGHT) return "too-far";
  return "ready";
}

/**
 * The measurements every outline is derived from, or `null`.
 *
 * All four landmarks, every one of them actually seen. Both of each pair,
 * because every position below is a midpoint and a midpoint is unchanged by
 * mirroring — so the detector's idea of which arm is which cannot reach the
 * drawing.
 */
function torsoOf(reading: PoseReading) {
  if (!reading) return null;
  const { leftShoulder, rightShoulder, leftHip, rightHip } = reading;
  if (
    !isObserved(leftShoulder) ||
    !isObserved(rightShoulder) ||
    !isObserved(leftHip) ||
    !isObserved(rightHip)
  ) {
    return null;
  }

  const shoulders = midpoint(leftShoulder, rightShoulder);
  const hips = midpoint(leftHip, rightHip);
  const height = Math.abs(hips.y - shoulders.y);
  const width = Math.abs(leftHip.x - rightHip.x);
  if (!Number.isFinite(height) || !Number.isFinite(width) || width <= 0) {
    return null;
  }
  return { shoulders, hips, height, width };
}

/**
 * Where the drawn area is centred, along the shoulder-to-hip line.
 *
 * There used to be a second outline here, marking the navel to be avoided. It
 * is gone, and the reason is worth keeping: **the navel is not detected.** The
 * model has no landmark for it, so it was being placed at a fraction of the
 * torso — an estimate, drawn as a ring, labelled with the name of an anatomical
 * landmark. On most bodies it did not land on one, which is what it looked
 * like.
 *
 * Pointing at a specific landmark and missing is a different kind of wrong from
 * indicating a broad region: a region tolerates the error it was always going
 * to have, and a named point does not. She can see her own navel; the step's
 * words already say to stay clear of it. So the overlay shows the one thing she
 * cannot judge by looking — roughly where on her the step is pointing — and
 * claims nothing else.
 */
const AREA_FRACTION = 0.82;

/**
 * The drawn area's **radii** — half-extents, not extents.
 *
 * They were `0.62` and `0.2` with a comment calling them "fractions of hip
 * width and torso height", which is what they would be if they were extents.
 * Used as radii they drew an ellipse 1.24× the width of her hips, spilling onto
 * the background, and reaching from 0.64 to 1.04 of the way down her torso —
 * past the hip landmarks, into the inguinal region, rather than the lower
 * abdomen the step names.
 *
 * Halving them fixed the spill and overshot: the area read as a small target on
 * a region the step describes in words as broad, and a target invites precision
 * this geometry cannot deliver. Widened back to 84% of her hip width and a
 * quarter of her torso, which keeps both properties that matter — it stays
 * inside her silhouette rather than on the background, and its lower edge stays
 * above the hip landmarks rather than reaching the inguinal region. Those two
 * are asserted; the exact size between them is a design call.
 */
const AREA_WIDTH = 0.42;
const AREA_HEIGHT = 0.13;

export type Point = { x: number; y: number };

export type InjectionZones = {
  /** Centre of the area the step points at, in box space, already mirrored. */
  area: Point;
  /** Radii as fractions of the box's own width and height. */
  areaRadius: { rx: number; ry: number };
};

/**
 * A point in camera-frame space, moved into preview-box space under `cover`.
 *
 * `cover` scales the frame until it fills both axes and lets the surplus hang
 * off the edges. Whichever axis has surplus is the one whose coordinates
 * compress: half the frame's width can be off-screen, so a landmark at `0.25`
 * may be at `0` in the box, or off it entirely.
 *
 * Aspect ratios are width ÷ height. A frame or box with a nonsensical ratio
 * returns the point untouched, which is the same as assuming they match — the
 * behaviour before this existed, and a safer failure than a divide by zero.
 */
export function mapFrameToBox(
  point: Point,
  frameAspect: number,
  boxAspect: number,
): Point {
  if (
    !Number.isFinite(frameAspect) ||
    !Number.isFinite(boxAspect) ||
    frameAspect <= 0 ||
    boxAspect <= 0
  ) {
    return point;
  }

  if (frameAspect > boxAspect) {
    // The frame is wider than the box: it overflows horizontally, so the
    // visible slice of the frame is narrower than the whole of it.
    const visible = boxAspect / frameAspect;
    return { x: (point.x - (1 - visible) / 2) / visible, y: point.y };
  }

  // Taller than the box, so the overflow is vertical.
  const visible = frameAspect / boxAspect;
  return { x: point.x, y: (point.y - (1 - visible) / 2) / visible };
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Where along the leg the band sits, and how big it is — all three read off the
 * drawing rather than chosen again here.
 *
 * The outline and the measured band have to be the same band, or she is shown
 * one thing while she aims and a different one while she injects. Two sets of
 * constants that happen to agree today is how that stops being true. So the
 * fraction and both extents are divided out of the drawing's own numbers, and
 * `seatedLeg.ts` is where the argument for them lives — including the one that
 * is a clinical claim (the upper half of the middle third) rather than a
 * drawing decision.
 */
const THIGH_LENGTH = LEG_AXIS.kneeX - LEG_AXIS.hipX;
const THIGH_FRACTION = (LEG_BAND.cx - LEG_AXIS.hipX) / THIGH_LENGTH;
const THIGH_ALONG = LEG_BAND.rx / THIGH_LENGTH;
const THIGH_ACROSS = LEG_BAND.ry / THIGH_LENGTH;
/**
 * How far toward the front of the thigh the band sits, in leg lengths.
 *
 * Divided out of the drawing like the rest. It is applied straight up the
 * screen, with no reference to the measured leg's angle, and that is safe for
 * the reason the drawing hard-codes it: a seated thigh's quadriceps face the
 * ceiling whichever way she has turned the chair. What it keeps the band off is
 * the back of the thigh, where the sciatic nerve is.
 */
const THIGH_ANTERIOR = (LEG_AXIS.y - LEG_BAND.cy) / THIGH_LENGTH;

/**
 * The hip and knee of one leg, when the detector saw both.
 *
 * Same side, never a midpoint: she is injecting one thigh, and averaging her
 * two would put the outline between her legs.
 *
 * Which side is **her answer** when she gave one. The detector's confidence
 * decided it before, and that is a reasonable guess with one leg in shot and a
 * silent mistake with two: it would outline the leg it can see best rather than
 * the leg she is about to inject, and nothing on screen would say so. The
 * confidence heuristic survives only as the fallback for the moment her chosen
 * knee is out of frame — better the wrong leg than no leg, since the framing
 * screen is what tells her the shot is wrong either way.
 */
function thighOf(reading: PoseReading, chosen?: ThighSide | null) {
  if (!reading) return null;
  const { leftHip, rightHip, leftKnee, rightKnee } = reading;

  // One hip and one knee, on the same side, and nothing else. Both hips used to
  // be required, because the band's width was a fraction of her hip separation.
  // It is not any more, and the requirement outlived its reason in the worst
  // place: seated side-on and close enough for her leg to fill the picture, her
  // far hip is behind her near one or out of shot, so the band stopped being
  // measured at all and she was left looking at the drawn one — anchored to the
  // outline, not to her, which is exactly the frozen mark Anaïs reported.
  const sides = [
    { side: "left" as const, hip: leftHip, knee: leftKnee },
    { side: "right" as const, hip: rightHip, knee: rightKnee },
  ].filter((side) => isObserved(side.hip) && isObserved(side.knee));
  if (sides.length === 0) return null;

  // Her answer, or nothing. There was a fallback here — if the side she named
  // was not observed, the more confident knee won — and it is the exact bug
  // `thighSide.ts` says the question was added to remove, reintroduced one
  // level down: the band lands on her other thigh with nothing on screen
  // saying so. Worse, `framingFrom` reads this same function, so it answered
  // "ready" *because* the fallback had succeeded, and the check that was
  // supposed to catch it was the thing hiding it.
  //
  // So a named side that is not in frame is `null`, which every caller already
  // means "say you cannot see her, and draw nothing".
  const best = chosen
    ? sides.find((candidate) => candidate.side === chosen)
    : sides.reduce((a, b) =>
        (b.knee?.visibility ?? 1) > (a.knee?.visibility ?? 1) ? b : a,
      );
  const knee = best?.knee;
  if (!best || !knee) return null;

  const length = Math.hypot(knee.x - best.hip.x, knee.y - best.hip.y);
  // Her hip separation used to be required here, because the band's width was
  // a fraction of it. Seen from the side her two hips are one point, so that
  // guard rejected the exact posture this guide asks her to adopt — and the
  // band it was protecting no longer exists. What is left needs a hip and a
  // knee, which is what the framing message claims to be checking.
  if (!Number.isFinite(length) || length <= 0) return null;

  return { hip: best.hip, knee, length };
}

/**
 * The band on her own thigh, kept inside what the outline says is a thigh.
 *
 * ### Why it is measured at all
 *
 * It was not, for one commit. The band was drawn on the silhouette, on the
 * reasoning that she puts her leg inside the outline so the outline knows where
 * the band goes. Anaïs's correction: *"il faudrait tout de même que la zone
 * d'injection repère la bonne zone dans la vidéo, mais pas qu'elle fasse de
 * mouvements irrationnels sachant le patron de la cuisse."* Both halves are
 * the point. A mark that ignores her leg is a sticker on the glass; a mark that
 * follows a noisy detector anywhere is worse than a sticker. So the position is
 * hers and the outline is what makes it plausible.
 *
 * ### The shape does not follow the measurement
 *
 * Only the centre does. The size comes from how long her leg is on screen —
 * a stable scalar — and the orientation is the outline's, which is level,
 * because level is the posture this guide asks for and the one she has lined
 * herself up with. Deriving the orientation from the measured hip→knee axis
 * instead is what made the band morph: a couple of degrees of landmark noise
 * swap a little of the long extent for a little of the short one, every frame,
 * and an ellipse that breathes between two shapes reads as broken rather than
 * as tracking.
 *
 * ### What is *not* constrained
 *
 * The position. It was briefly clamped into the outline's thigh, on the theory
 * that a mis-detected knee should not drag the band off her leg — and the band
 * read as pinned to the drawing rather than to her, which is the opposite of
 * what this is for. The abdomen has no such fence and does not need one: a bad
 * reading is a fast move, `trackZones` is already what decides how fast the
 * drawing is allowed to follow one, and eight missed readings drop the outline
 * entirely. That is the mechanism, and the thigh uses the same one.
 */
export function thighZoneFrom(
  reading: PoseReading,
  frameAspect: number,
  boxAspect: number,
  side?: ThighSide | null,
): InjectionZones | null {
  const thigh = thighOf(reading, side);
  if (!thigh) return null;
  // The same floor the framing message uses, so a band cannot appear on a leg
  // the aiming screen is still calling too far away.
  if (thigh.length < MIN_THIGH_LENGTH) return null;

  const centre = {
    x: thigh.hip.x + (thigh.knee.x - thigh.hip.x) * THIGH_FRACTION,
    y: thigh.hip.y + (thigh.knee.y - thigh.hip.y) * THIGH_FRACTION,
  };
  const mapped = mapFrameToBox(centre, frameAspect, boxAspect);
  const screen = { x: toMirroredScreenX(mapped.x), y: mapped.y };

  // Her leg's length **in the box**, in units of the box's own width.
  //
  // Measured after the crop rather than before it, which is the whole of the
  // correction here. It used to be `hypot` on frame coordinates, multiplied by
  // one stretch for `rx` and the other for `ry` — and on a level leg that
  // quantity is a fraction of frame *width*, so `ry` converted a width into a
  // height with the wrong factor. The band's on-screen depth then came out the
  // same on a 4:3 webcam and a 16:9 one while her leg was half again as long,
  // 44% shallower than the drawing specifies, and its anterior offset shrank
  // with it — toward the back of the thigh, the one direction this is supposed
  // to stay away from. Doing the measurement in box space removes both stretch
  // factors and the class of mistake with them.
  const hipBox = mapFrameToBox(thigh.hip, frameAspect, boxAspect);
  const kneeBox = mapFrameToBox(thigh.knee, frameAspect, boxAspect);
  // A box-height fraction is `boxAspect` box-widths, since `boxAspect` is
  // width ÷ height. Everything below is in box widths until it is handed back.
  const acrossBox = (kneeBox.y - hipBox.y) / (boxAspect > 0 ? boxAspect : 1);
  const lengthInBoxWidths = Math.hypot(kneeBox.x - hipBox.x, acrossBox);
  if (!Number.isFinite(lengthInBoxWidths) || lengthInBoxWidths <= 0) {
    return null;
  }

  const toBoxHeight = boxAspect > 0 ? boxAspect : 1;

  return {
    area: {
      x: screen.x,
      y: screen.y - lengthInBoxWidths * THIGH_ANTERIOR * toBoxHeight,
    },
    // Level, like the outline, and sized by her leg. See the note above on why
    // the measured axis is not used for this.
    areaRadius: {
      rx: lengthInBoxWidths * THIGH_ALONG,
      ry: lengthInBoxWidths * THIGH_ACROSS * toBoxHeight,
    },
  };
}

/**
 * The outlines to draw, or `null` when the camera cannot support them.
 *
 * `null` on exactly the readings `framingFrom` refuses — both funnel through
 * `torsoOf`, so the preview cannot show an outline while the framing step is
 * still saying it cannot see her. `pose-zones.test.ts` asserts the biconditional
 * rather than restating it.
 *
 * @param frameAspect the camera frame's width ÷ height
 * @param boxAspect the preview box's width ÷ height
 */
export function injectionZonesFrom(
  reading: PoseReading,
  frameAspect: number,
  boxAspect: number,
): InjectionZones | null {
  const torso = torsoOf(reading);
  if (!torso) return null;
  const { shoulders, hips, height: torsoHeight, width: hipWidth } = torso;
  if (torsoHeight < MIN_TORSO_HEIGHT) return null;

  const along = (fraction: number): Point => ({
    x: shoulders.x + (hips.x - shoulders.x) * fraction,
    y: shoulders.y + (hips.y - shoulders.y) * fraction,
  });

  // Mirrored last, and only once. Radii are distances and are unaffected by a
  // flip; only the centres move.
  const toScreen = (point: Point): Point => {
    const mapped = mapFrameToBox(point, frameAspect, boxAspect);
    return { x: toMirroredScreenX(mapped.x), y: mapped.y };
  };

  // Widths compress with the same factor the x coordinates do, so a radius
  // measured in frame space has to be scaled the same way or the outline will
  // be the right shape in the wrong size.
  const horizontalStretch =
    frameAspect > boxAspect && boxAspect > 0 ? frameAspect / boxAspect : 1;
  const verticalStretch =
    boxAspect > frameAspect && frameAspect > 0 ? boxAspect / frameAspect : 1;

  return {
    area: toScreen(along(AREA_FRACTION)),
    areaRadius: {
      rx: hipWidth * AREA_WIDTH * horizontalStretch,
      ry: torsoHeight * AREA_HEIGHT * verticalStretch,
    },
  };
}

/**
 * Holding the outlines still.
 *
 * Raw output is unusable: landmarks wander a few pixels every frame, so at
 * twelve readings a second the outlines shiver, and one bad reading makes them
 * vanish and come back. Both were described, accurately, as it never settling.
 *
 * Two corrections, and they answer different complaints:
 *
 * - **Smoothing** eases each number toward the new reading instead of jumping
 *   to it, at a rate chosen from how far the reading asks it to move — calm
 *   enough to absorb the shiver of a still body, quick enough to follow a real
 *   one. A single rate could not do both, and the one that was here did
 *   neither. See `gainFor`.
 * - **Holding** keeps the last good outline through a short run of failures
 *   rather than dropping it on the first. Counted in readings rather than
 *   measured in seconds, so this stays a pure function of what the detector
 *   said: no clock, no timers, testable.
 *
 * The hold is deliberately short. An outline that lingers after she has left
 * the frame is an outline pointing at nothing, which is the failure the whole
 * observation rule exists to prevent — so it survives a stutter and not an
 * absence.
 */

export type ZoneTracker = {
  /** What to draw, already smoothed. */
  zones: InjectionZones | null;
  /** Consecutive readings that supported no outline. */
  misses: number;
};

export const EMPTY_TRACKER: ZoneTracker = { zones: null, misses: 0 };

/**
 * How far toward a new reading to move, decided by how far it asks to move.
 *
 * A single rate cannot serve both jobs. One low enough to absorb the shiver —
 * landmarks wander a few thousandths of the box every reading, forever, while
 * she stands perfectly still — is too slow to follow her when she actually
 * leans in. One fast enough to follow her draws the shiver faithfully. The old
 * fixed `0.2` sat between the two and did neither: the outline kept moving on a
 * still body, which is what reads as it never settling.
 *
 * So the rate is chosen per reading from the distance being asked for. Below
 * `STILL` the movement is noise and is answered at `CALM`, which averages it
 * away; above `MOVING` it is her, and is answered at `QUICK`. Between the two
 * the rate ramps, so there is no visible threshold.
 *
 * Because the rate follows the *error* rather than a velocity, it is
 * self-correcting: a slow genuine drift accumulates error until the rate rises
 * to meet it. The outline therefore sits within about `STILL` of the truth —
 * under two percent of the box, a few pixels — and no closer, on purpose.
 *
 * Measured against the readings a propped phone actually produces: the drawn
 * centre wanders 3.3 thousandths of the box while she is still, against 9 with
 * the fixed rate, and it is *faster* to settle after a real move — within two
 * percent of the box in a third of a second, against nine tenths.
 */
const CALM_GAIN = 0.04;
const QUICK_GAIN = 0.5;
const STILL = 0.014;
const MOVING = 0.045;

/**
 * How still a drawing is held, and how hard it is to convince it she has moved.
 *
 * Two of these, because a torso and a limb are not equally noisy and pretending
 * otherwise is what made the thigh band restless (Anaïs, 2026-09-24).
 *
 * The abdomen's centre is a midpoint of four landmarks, so their independent
 * jitter largely cancels in it. The thigh's is a midpoint of two, on one side,
 * one of which is a knee — the least reliable of the six the coach reads,
 * because a seated knee is the point most often half-occluded by her own hand
 * or the edge of a chair. Noise there does not cancel; it lands on the drawing.
 * The size is worse again: it is scaled by hip→knee distance, and a distance is
 * a *difference* of two noisy points, where noise adds.
 *
 * So the limb profile has roughly twice the deadband and a little over half the
 * calm gain. `QUICK_GAIN` is untouched in both: a real move must still be
 * followed immediately, and a drawing that lags her leg is a different and
 * worse failure than one that shivers.
 *
 * The torso numbers are exactly the ones measured against real readings when
 * this was written — see the note above — and nothing here changes them.
 */
export type ZoneTracking = {
  calmGain: number;
  quickGain: number;
  still: number;
  moving: number;
};

export const TORSO_TRACKING: ZoneTracking = {
  calmGain: CALM_GAIN,
  quickGain: QUICK_GAIN,
  still: STILL,
  moving: MOVING,
};

export const LIMB_TRACKING: ZoneTracking = {
  calmGain: 0.025,
  quickGain: QUICK_GAIN,
  still: 0.03,
  moving: 0.09,
};

/** Readings to keep the last outline through before giving it up. */
const HOLD_READINGS = 8;

function gainFor(travel: number, tracking: ZoneTracking): number {
  if (travel <= tracking.still) return tracking.calmGain;
  if (travel >= tracking.moving) return tracking.quickGain;
  const ramp = (travel - tracking.still) / (tracking.moving - tracking.still);
  return tracking.calmGain + (tracking.quickGain - tracking.calmGain) * ramp;
}

function ease(from: number, to: number, gain: number): number {
  return from + (to - from) * gain;
}

function easePoint(from: Point, to: Point, gain: number): Point {
  return { x: ease(from.x, to.x, gain), y: ease(from.y, to.y, gain) };
}

function easeRadius(
  from: { rx: number; ry: number },
  to: { rx: number; ry: number },
  gain: number,
): { rx: number; ry: number } {
  return { rx: ease(from.rx, to.rx, gain), ry: ease(from.ry, to.ry, gain) };
}

export function trackZones(
  previous: ZoneTracker,
  next: InjectionZones | null,
  tracking: ZoneTracking = TORSO_TRACKING,
): ZoneTracker {
  if (next) {
    // The first outline after nothing is taken whole. Easing into it from a
    // stale position would slide it across her body while she watches.
    if (!previous.zones) return { zones: next, misses: 0 };

    // One rate for the whole outline, taken from how far its centre is being
    // asked to move. The radii follow the same decision because they belong to
    // the same body: letting them settle at their own pace would breathe the
    // outline in and out while its centre held still.
    // The largest of the three, not the centre alone. A centre is a midpoint of
    // a pair, so noise averages down in it; a radius is a difference, so noise
    // adds. And when she rotates her torso to reach the site, her hips
    // foreshorten while their midpoint barely moves — the centre would report
    // "she is still" and hold the ellipse at its old width, visibly too wide on
    // her, through the injection.
    const gain = gainFor(
      Math.max(
        Math.hypot(
          next.area.x - previous.zones.area.x,
          next.area.y - previous.zones.area.y,
        ),
        Math.abs(next.areaRadius.rx - previous.zones.areaRadius.rx),
        Math.abs(next.areaRadius.ry - previous.zones.areaRadius.ry),
      ),
      tracking,
    );

    return {
      zones: {
        area: easePoint(previous.zones.area, next.area, gain),
        areaRadius: easeRadius(
          previous.zones.areaRadius,
          next.areaRadius,
          gain,
        ),
      },
      misses: 0,
    };
  }

  const misses = previous.misses + 1;
  if (misses >= HOLD_READINGS) return { zones: null, misses };
  return { zones: previous.zones, misses };
}
